import * as React from "react";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Collapse from "@mui/material/Collapse";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import { MenuItemLink, usePermissions } from "react-admin";
import icons from "../../styles/icons/icons";
import { useNavigate } from "react-router-dom";
import UserContext from "../../contexts/UserContext";

interface NestedMenuItemProps {
  name: string;
  icon: React.ReactNode;
  children: React.ReactNode;
}

interface NestedMenuItemLinkProps {
  to: string;
  primaryText: string;
  leftIcon: React.ReactNode;
}

const NestedMenuItem: React.FC<NestedMenuItemProps> = ({
  name,
  icon,
  children,
}) => {
  const [open, setOpen] = React.useState(false);

  const handleClick = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  return (
    <>
      <ListItemButton onClick={handleClick}>
        <ListItemIcon>{icon}</ListItemIcon>
        <ListItemText primary={name} />
        {open ? <ExpandLess /> : <ExpandMore />}
      </ListItemButton>
      <Collapse in={open} timeout="auto" unmountOnExit>
        <List component="div" disablePadding>
          {children}
        </List>
      </Collapse>
    </>
  );
};

const NestedMenuItemLink: React.FC<NestedMenuItemLinkProps> = ({
  to,
  primaryText,
  leftIcon,
}) => (
  <ListItemButton sx={{ pl: 5 }}>
    <MenuItemLink
      to={to}
      primaryText={primaryText}
      leftIcon={React.isValidElement(leftIcon) ? leftIcon : undefined}
    />
  </ListItemButton>
);
export default function NestedMenu() {
  let navigate = useNavigate();
  const { isGroupAdmin, isOrgAdmin, isScheduler, isSchAdmin } =
    React.useContext(UserContext);
  const { permissions } = usePermissions();
  const roles: string[] = React.useMemo(
    () => (permissions as any)?.resource_access?.account?.roles ?? [],
    [permissions]
  );
  const hasFinancial = roles.includes("financial");
  return (
    <List
      sx={{ width: "100%", maxWidth: 360, bgcolor: "background.paper" }}
      component="nav"
    >
      <NestedMenuItem name="调度" icon={<icons.scheduling />}>
        <NestedMenuItemLink
          to="/schedulings"
          primaryText="调度推荐"
          leftIcon={<icons.recommend />}
        />
        {isScheduler && (
          <>
            <NestedMenuItemLink
              to="/driverSchedules"
              primaryText="调度追踪"
              leftIcon={<icons.schedule />}
            />
            <NestedMenuItemLink
              to="/calendar"
              primaryText="调度日历"
              leftIcon={<icons.scheduling />}
            />
            <NestedMenuItemLink
              to="/cargos/price"
              primaryText="参考价管理"
              leftIcon={<icons.scheduling />}
            />
          </>
        )}
      </NestedMenuItem>
      {isScheduler && (
        <NestedMenuItem name="智能体" icon={<icons.agent />}>
          <NestedMenuItemLink
            to="/agent/sessions"
            primaryText="任务会话"
            leftIcon={<icons.agent />}
          />
          <NestedMenuItemLink
            to="/agent/artifacts/leads"
            primaryText="货主线索"
            leftIcon={<icons.agent />}
          />
          <NestedMenuItemLink
            to="/agent/dispatch-records"
            primaryText="智能体操作记录"
            leftIcon={<icons.agent />}
          />
          <NestedMenuItemLink
            to="/agentLogs"
            primaryText="智能体日志"
            leftIcon={<icons.agent />}
          />
        </NestedMenuItem>
      )}
      <NestedMenuItem name="App" icon={<icons.app />}>
        <NestedMenuItemLink
          to="/drivers"
          primaryText="App司机"
          leftIcon={<icons.drivers />}
        />
        <NestedMenuItemLink
          to="/map/map_drivers"
          primaryText="地图司机"
          leftIcon={<icons.drivers />}
        />
        <NestedMenuItemLink
          to="/map/all-drivers-map"
          primaryText="全图司机"
          leftIcon={<icons.drivers />}
        />
        <NestedMenuItemLink
          to="/validCargos"
          primaryText="App货物"
          leftIcon={<icons.cargos />}
        />
        <NestedMenuItemLink
          to="/waybills"
          primaryText="App运单"
          leftIcon={<icons.orders />}
        />
        {hasFinancial && (
          <NestedMenuItemLink
            to="/billings"
            primaryText="结算账单"
            leftIcon={<icons.orders />}
          />
        )}
        {isScheduler && (
          <NestedMenuItemLink
            to="/appTrack"
            primaryText="用户行为"
            leftIcon={<icons.analytics />}
          />
        )}
      </NestedMenuItem>
      <ListItemButton onClick={() => navigate("/communication")}>
        <ListItemIcon>
          <icons.communication />
        </ListItemIcon>
        <ListItemText primary="沟通管理" />
        <ListItemIcon />
      </ListItemButton>
      <NestedMenuItem name="人工审核" icon={<icons.app />}>
        <NestedMenuItemLink
          to="/verify/user"
          primaryText="实名认证列表"
          leftIcon={<icons.review />}
        />
        <NestedMenuItemLink
          to="/verify/truck"
          primaryText="车辆认证列表"
          leftIcon={<icons.review />}
        />
        <NestedMenuItemLink
          to="/manualReview"
          primaryText="人工认证审核"
          leftIcon={<icons.review />}
        />
      </NestedMenuItem>
      <NestedMenuItem name="用户管理" icon={<icons.list />}>
        <NestedMenuItemLink
          to="/users/usets"
          primaryText="用户列表"
          leftIcon={<icons.list />}
        />
      </NestedMenuItem>
      <>
        {isScheduler && (
          <>
            <NestedMenuItem name="车队" icon={<icons.group />}>
              <NestedMenuItemLink
                to="/fleets"
                primaryText="车队"
                leftIcon={<icons.group />}
              />
              <NestedMenuItemLink
                to="/stations"
                primaryText="站点"
                leftIcon={<icons.station />}
              />
              <NestedMenuItemLink
                to="/msDrivers"
                primaryText="多渠道司机"
                leftIcon={<icons.msDrivers />}
              />
              <NestedMenuItemLink
                to="/msTrucks"
                primaryText="多渠道货车"
                leftIcon={<icons.msTrucks />}
              />
            </NestedMenuItem>
            <ListItemButton onClick={() => navigate("/reserveCapacity")}>
              <ListItemIcon>
                <icons.reserve />
              </ListItemIcon>
              <ListItemText primary="储备运力" />
              <ListItemIcon />
            </ListItemButton>
            <NestedMenuItem name="推广" icon={<icons.notification />}>
              <NestedMenuItemLink
                to="/push"
                primaryText="推送记录"
                leftIcon={<icons.history />}
              />
            </NestedMenuItem>
            <NestedMenuItem name="统计" icon={<icons.analytics />}>
              <NestedMenuItemLink
                to="/users-cargo-stats"
                primaryText="发货统计"
                leftIcon={<icons.shoppingCart />}
              />
            </NestedMenuItem>
          </>
        )}
      </>

      {isGroupAdmin && (
        <ListItemButton onClick={() => navigate("/groups")}>
          <ListItemIcon>
            <icons.groups />
          </ListItemIcon>
          <ListItemText primary="部门管理" />
          <ListItemIcon />
        </ListItemButton>
      )}
      {isOrgAdmin && (
        <ListItemButton onClick={() => navigate("/organizations")}>
          <ListItemIcon>
            <icons.bussiness />
          </ListItemIcon>
          <ListItemText primary="企业管理" />
          <ListItemIcon />
        </ListItemButton>
      )}
      {isSchAdmin && (
        <NestedMenuItem name="授权管理" icon={<icons.settingsIcon />}>
          <NestedMenuItemLink
            to="/thirdPartyClients"
            primaryText="第三方客户端管理"
            leftIcon={<icons.settingsIcon />}
          />
          <NestedMenuItemLink
            to="/thirdDataRequests"
            primaryText="预授权管理"
            leftIcon={<icons.settingsIcon />}
          />
        </NestedMenuItem>
      )}
    </List>
  );
}
